import { AutodocType } from '@sonar-software/sonar-core';
import { SuggestionField } from './SuggestionField';
import { UseSuggestionEditorResult } from '@hooks';
import { PlaintextEditor, PlaintextEditorProps, PlaintextEditorRef } from './PlaintextEditor';
import React, { useCallback, useRef, useState } from 'react';

export type SuggestPlaintextEditorProps = PlaintextEditorProps & {
	autodocType: AutodocType;
	sonarObjectId: number;
	suggestion: UseSuggestionEditorResult;
};

export const SuggestPlaintextEditor: React.FC<SuggestPlaintextEditorProps> = ({
	autodocType,
	isReadOnly,
	onSave,
	placeholder,
	sonarObjectId,
	suggestion,
	value,
	...rest
}) => {
	const editorRef = useRef<PlaintextEditorRef>(null);
	const [ editingValue, setEditingValue ] = useState('');

	const onEdit = useCallback(() => {
		suggestion.accept();
		editorRef.current?.edit();
	}, [ suggestion ]);

	const onAccept = useCallback(() => {
		suggestion.accept();
		editorRef.current?.save();
	}, [ suggestion ]);

	const onFocus = useCallback(() => {
		suggestion.accept();
	}, [ suggestion ]);

	const onBlur = useCallback(() => {
		const userInput = !suggestion.value && editingValue;
		if (!(suggestion.value && suggestion.accepted) && !userInput) {
			suggestion.reset();
		}
	}, [ suggestion, editingValue ]);

	const onCancel = useCallback(() => {
		suggestion.reset();
	}, [ suggestion ]);

	const onChange = useCallback((newValue: string) => {
		setEditingValue(newValue);
	}, []);

	const handleSave = useCallback(
		async(newValue: string) => {
			const result = onSave(newValue);

			if (result?.then) {
				await result;
			}

			suggestion.reset();
		},
		[ onSave, suggestion ]
	);

	return (
		<SuggestionField
			autodocType={ autodocType }
			isReadOnly={ isReadOnly }
			onAccept={ onAccept }
			onBlur={ onBlur }
			onEdit={ onEdit }
			placeholder={ placeholder }
			suggestion={ suggestion }>
			<PlaintextEditor
				{ ...rest }
				isReadOnly={ isReadOnly }
				onBlur={ onBlur }
				onCancel={ onCancel }
				onChange={ onChange }
				onFocus={ onFocus }
				onSave={ handleSave }
				placeholder={ placeholder }
				ref={ editorRef }
				value={ suggestion.editorValue } />
		</SuggestionField>
	);
};
